import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, shareReplay, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CelumPropertiesProvider } from '@celum/core/configuration';
import { numericHash } from '@celum/core/util';
import { DateUtil } from '@celum/ng2base/util';

import { AuthService } from './auth.service';
import {
  AuthContext,
  AuthToken,
  isB2CAccessTokenRequestDto,
  isB2CRequest,
  isB2CTokenRequestDto,
  isServiceAccessTokenRequestDto,
  RequestOptionsForSingleToken,
  ServiceTokenRequestDto,
  TOKEN_REFRESH_LEAD_TIME_IN_MS,
  TokenRequestDto
} from '../model/auth-token.model';

export interface TokenCache {
  localStorageKey: string;
  cachedTokens: any;
  dtoHash: number;
  cachedToken: any;
}

// this http context token indicates that the http call to the token endpoint was issued by the ServiceAccessTokenProvider.
// interceptors can act accordingly (e.g. skip interception).
export const ISSUED_BY_SERVICE_ACCESS_TOKEN_PROVIDER = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ServiceAccessTokenProvider {
  private readonly TOKEN_KEY = 'Celum_Service_AccessTokens';
  private tokenRequestsInProgress = new Map<number, Observable<string>>();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  public getServiceAccessToken<T extends TokenRequestDto>(requestDto: T, opts?: RequestOptionsForSingleToken): Observable<string> {
    if (!requestDto) {
      return throwError(() => new Error('No ServiceAccessTokenDto provided'));
    }

    const isServiceAccessTokenAvailable = this.hasServiceAccessToken(requestDto, opts);

    if (opts?.noRefetch && !isServiceAccessTokenAvailable) {
      return of(null);
    }

    // caching is handled by msal lib in case of b2c and b2c access tokens
    if (isB2CTokenRequestDto(requestDto)) {
      return this.authService.getB2CToken();
    }

    if (isB2CAccessTokenRequestDto(requestDto)) {
      return this.authService.getB2CAccessToken(requestDto);
    }

    return this.getCustomToken(requestDto, opts);
  }

  public hasServiceAccessToken<T extends TokenRequestDto>(requestDto: T, opts?: RequestOptionsForSingleToken): boolean {
    if (!requestDto) {
      throw new Error('No ServiceAccessTokenDto provided');
    }

    if (isB2CRequest(requestDto) && this.authService.isB2CTokenStillValid()) {
      return true;
    }

    if (!isServiceAccessTokenRequestDto(requestDto)) {
      return false;
    }

    const { cachedToken } = this.getCachedToken(requestDto, opts?.context);

    const leadTimeInMs = opts?.tokenLeadTimeInMilliseconds ?? TOKEN_REFRESH_LEAD_TIME_IN_MS;
    return ServiceAccessTokenProvider.isStillValid(cachedToken?.expiresAt, leadTimeInMs);
  }

  public putTokenToCache(requestDto: ServiceTokenRequestDto, context: AuthContext, token: AuthToken): void {
    const { localStorageKey, cachedTokens, dtoHash } = this.getCachedToken(requestDto, context);
    cachedTokens[dtoHash] = token;
    localStorage.setItem(localStorageKey, JSON.stringify(cachedTokens));
  }

  public clearTokenCache(clientId: string): void {
    localStorage.removeItem(this.getLocalStorageKey(clientId));
  }

  private getCustomToken(requestDto: ServiceTokenRequestDto, opts?: RequestOptionsForSingleToken): Observable<string> {
    const { dtoHash, cachedToken } = this.getCachedToken(requestDto, opts?.context);

    if (ServiceAccessTokenProvider.isStillValid(cachedToken?.expiresAt, opts?.tokenLeadTimeInMilliseconds ?? TOKEN_REFRESH_LEAD_TIME_IN_MS)) {
      return of(cachedToken.token);
    }

    // we have a cached token and even though it's not valid anymore, we do not want to refetch it because "noRefetch" is set
    if (cachedToken && opts?.noRefetch) {
      return of(null);
    }

    if (!this.tokenRequestsInProgress.has(dtoHash)) {
      const tokenEndpoint = opts?.endpoint || `${CelumPropertiesProvider.properties.authentication.saccUrl}/auth/token`;

      this.tokenRequestsInProgress.set(
        dtoHash,
        this.http
          .post<AuthToken>(
            tokenEndpoint,
            { ...requestDto, ...opts?.context },
            { context: new HttpContext().set(ISSUED_BY_SERVICE_ACCESS_TOKEN_PROVIDER, true) }
          )
          .pipe(
            map(serviceAccessToken => {
              this.putTokenToCache(requestDto, opts?.context, serviceAccessToken);
              this.tokenRequestsInProgress.delete(dtoHash);
              return serviceAccessToken.token;
            }),
            catchError(error => {
              console.error('Something went wrong during acquiring the service access token.', error);
              this.tokenRequestsInProgress.delete(dtoHash);
              return of('');
            }),
            shareReplay(1)
          )
      );
    }

    return this.tokenRequestsInProgress.get(dtoHash);
  }

  private getCachedToken<T extends ServiceTokenRequestDto>(requestDto: T, authContext?: AuthContext): TokenCache {
    const localStorageKey = this.getLocalStorageKey(requestDto.clientId);
    const localStorageCachedTokens = localStorage.getItem(localStorageKey);
    const cachedTokens = JSON.parse(localStorageCachedTokens) || {};
    const dtoHash = numericHash(JSON.stringify({ ...requestDto, ...authContext }));
    const cachedToken = cachedTokens[dtoHash];
    return {
      localStorageKey,
      cachedTokens,
      dtoHash,
      cachedToken
    };
  }

  /**
   * Checks if the provided expiration date is still valid considering a lead time.
   *
   * @param now - current timestamp
   * @param expiresAt - ISO formatted date string
   * @param leadTimeInMs - the lead time in milliseconds
   *
   * @returns whether the expiration date is still valid
   */
  private static isStillValid(expiresAt: string, leadTimeInMs: number): boolean {
    if (!expiresAt) {
      return false;
    }

    return !DateUtil.isOverDue(new Date(expiresAt), leadTimeInMs);
  }

  private getLocalStorageKey(clientId: string): string {
    return `${this.TOKEN_KEY}_${this.authService.getMsalAccount()?.homeAccountId || 'public'}_${clientId}`;
  }
}
